import { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  Edit, 
  Trash2, 
  TrendingUp, 
  TrendingDown, 
  AlertTriangle,
  CheckCircle2,
  Clock,
  DollarSign,
  Users,
  BarChart3,
  Lightbulb,
  AlertCircle,
  Target,
  Activity,
  Maximize2,
} from 'lucide-react';
import { formatCurrency, formatHours, formatPercent, getProgressColor } from '@/lib/budget-utils';
import { cn } from '@/lib/utils';
import type { CostCentre } from './cost-centres-table';
import { 
  fetchCostCentreTransactions, 
  fetchCostCentreBreakdown,
  updateCostCentre,
  deleteCostCentre,
  type CostCentreTransaction,
  type CostCentreBreakdown,
} from '@/lib/data-service';

interface CostCentreDetailsPanelProps {
  costCentre: CostCentre | null;
  projectId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit?: (costCentre: CostCentre) => void;
  onDelete?: (costCentreId: number) => void;
  onMarkInactive?: (costCentreId: number) => void;
  onExpandToFullView?: () => void;
}

export function CostCentreDetailsPanel({
  costCentre,
  projectId,
  open,
  onOpenChange,
  onEdit,
  onDelete,
  onMarkInactive,
  onExpandToFullView,
}: CostCentreDetailsPanelProps) {
  const [activeTab, setActiveTab] = useState('details');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteDialogType, setDeleteDialogType] = useState<'has-transactions' | 'has-budget' | 'confirm'>('confirm');
  const [isEditMode, setIsEditMode] = useState(false);
    const [editFormData, setEditFormData] = useState<CostCentre | null>(null);
    const [isActive, setIsActive] = useState(true);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
  
    // State for cost centre transactions and breakdown (fetched from API)
    const [recentTransactions, setRecentTransactions] = useState<CostCentreTransaction[]>([]);
  const [costBreakdown, setCostBreakdown] = useState<CostCentreBreakdown>({
    labour: 0,
    expenses: 0,
    materials: 0,
    subcontractors: 0,
  });
  const [_isLoadingData, setIsLoadingData] = useState(false);

  // Reset edit mode when panel closes or cost centre changes
  useEffect(() => {
    if (!open) {
      setIsEditMode(false);
      setIsActive(costCentre?.isActive ?? true);
      setEditFormData(null);
    }
  }, [open, costCentre?.isActive]);

  useEffect(() => {
    if (costCentre) {
      setIsEditMode(false);
      setEditFormData(null);
    }
  }, [costCentre?.id]);

  // Reset edit mode when switching tabs
  useEffect(() => {
    if (activeTab !== 'details') {
      setIsEditMode(false);
      setEditFormData(null);
    }
  }, [activeTab]);

  // Fetch cost centre transactions and breakdown when cost centre changes
  useEffect(() => {
    if (!costCentre || !open) return;
    
    const fetchData = async () => {
      setIsLoadingData(true);
      try {
        const [transactions, breakdown] = await Promise.all([
          fetchCostCentreTransactions(costCentre.id, projectId),
          fetchCostCentreBreakdown(costCentre.id, projectId, costCentre.actualCosts),
        ]);
        setRecentTransactions(transactions);
        setCostBreakdown(breakdown);
      } catch {
        console.error('Failed to fetch cost centre data');
        // Reset to empty state on error
        setRecentTransactions([]);
        setCostBreakdown({
          labour: 0,
          expenses: 0,
          materials: 0,
          subcontractors: 0,
        });
      } finally {
        setIsLoadingData(false);
      }
    };
    
    fetchData();
  }, [costCentre?.id, projectId, costCentre?.actualCosts, open]);

  if (!costCentre) return null;

  // Handle entering edit mode
  const handleEnterEditMode = () => {
    setEditFormData({ ...costCentre });
    setIsEditMode(true);
  };

  // Handle canceling edit mode
  const handleCancelEdit = () => {
    setEditFormData(null);
    setIsEditMode(false);
  };

    // Handle saving edits
    const handleSaveEdit = async () => {
      if (!editFormData || !costCentre) return;
    
      setIsSaving(true);
      try {
        await updateCostCentre(editFormData.id, {
          id: editFormData.id,
          projectId: projectId,
          code: editFormData.code,
          name: editFormData.name,
          type: editFormData.type,
          status: editFormData.status,
          billable: editFormData.billable,
          owner: editFormData.owner,
          description: editFormData.description,
          costsBudget: editFormData.costsBudget,
          actualCosts: editFormData.actualCosts,
          committedCosts: editFormData.committedCosts,
          revenueBudget: editFormData.revenueBudget,
          actualRevenue: editFormData.actualRevenue,
          hoursBudget: editFormData.hoursBudget,
          actualHours: editFormData.actualHours,
          isActive: editFormData.isActive,
        });
      
        onEdit?.(editFormData);
        setIsEditMode(false);
        setEditFormData(null);
        toast.success('Cost centre updated successfully', {
          duration: 3000,
        });
      } catch (error) {
        console.error('Failed to update cost centre:', error);
        toast.error('Failed to update cost centre. Please try again.');
      } finally {
        setIsSaving(false);
      }
    };

    // Handle status toggle
    const handleStatusToggle = async (checked: boolean) => {
      if (!costCentre) return;
    
      setIsUpdatingStatus(true);
      const statusText = checked ? 'active' : 'inactive';
    
      try {
        // Optimistic update
        setIsActive(checked);
      
                // Call API to update the cost centre status
                await updateCostCentre(costCentre.id, {
                  id: costCentre.id,
                  projectId: projectId,
                  code: costCentre.code,
                  name: costCentre.name,
                  type: costCentre.type,
                  status: costCentre.status,
                  billable: costCentre.billable,
                  owner: costCentre.owner,
                  description: costCentre.description,
                  costsBudget: costCentre.costsBudget,
                  actualCosts: costCentre.actualCosts,
                  committedCosts: costCentre.committedCosts,
                  revenueBudget: costCentre.revenueBudget,
                  actualRevenue: costCentre.actualRevenue,
                  hoursBudget: costCentre.hoursBudget,
                  actualHours: costCentre.actualHours,
                  isActive: checked,
                });
      
                // Notify parent component of the update so it can refresh the table
                const updatedCostCentre = { ...costCentre, isActive: checked };
                onEdit?.(updatedCostCentre);
      
                toast.success(`Cost centre marked as ${statusText}`, {
                  duration: 3000,
                });
      } catch (error) {
        // Revert on error
        setIsActive(!checked);
        console.error('Failed to update cost centre status:', error);
        toast.error('Failed to update status. Please try again.');
      } finally {
        setIsUpdatingStatus(false);
      }
    };

  // Update form field
  const updateEditField = (field: keyof CostCentre, value: any) => {
    if (editFormData) {
      setEditFormData({ ...editFormData, [field]: value });
    }
  };

  // Check if cost centre has been used (has any actual costs, hours, or revenue)
  const isCostCentreUsed = (cc: CostCentre | null): boolean => {
    if (!cc) return false;
    return (cc.actualCosts > 0 || cc.actualHours > 0 || cc.actualRevenue > 0);
  };

  // Calculate metrics
  const totalCosts = costCentre.actualCosts + costCentre.committedCosts;
  const costVariance = costCentre.costsBudget - costCentre.actualCosts;
  const costPercentUsed = costCentre.costsBudget > 0 
    ? (costCentre.actualCosts / costCentre.costsBudget) * 100 
    : 0;
  const hoursVariance = costCentre.hoursBudget - costCentre.actualHours;
  const hoursPercentUsed = costCentre.hoursBudget > 0
    ? (costCentre.actualHours / costCentre.hoursBudget) * 100
    : 0;
  const remainingHours = costCentre.hoursBudget - costCentre.actualHours;
  
  // Revenue metrics
  const revenueVariance = costCentre.actualRevenue - costCentre.revenueBudget;
  const revenuePercentAchieved = costCentre.revenueBudget > 0 
    ? (costCentre.actualRevenue / costCentre.revenueBudget) * 100 
    : 0;
  const remainingRevenue = costCentre.revenueBudget - costCentre.actualRevenue;
  const profitMargin = costCentre.actualRevenue > 0
    ? ((costCentre.actualRevenue - costCentre.actualCosts) / costCentre.actualRevenue) * 100
    : 0;
  
  // Check if cost centre has any actuals (transactions)
  const hasTransactions = costCentre.actualCosts > 0 || costCentre.actualHours > 0 || costCentre.actualRevenue > 0;
  
  // Check if cost centre has any budgets
  const hasBudget = costCentre.costsBudget > 0 || costCentre.hoursBudget > 0 || costCentre.revenueBudget > 0;

  // Calculate burn rate (simplified - costs per month)
  const monthsElapsed = 3; // Approximate months elapsed
  const burnRate = costCentre.actualCosts / monthsElapsed;

  // Budget status color
  const getBudgetStatusColor = (percent: number) => {
    if (percent < 75) return 'text-success-600 dark:text-success-500';
    if (percent < 90) return 'text-warning-600 dark:text-warning-500';
    return 'text-error-600 dark:text-error-500';
  };

  const getBudgetStatusBg = (percent: number) => {
    if (percent < 75) return 'bg-success-50 dark:bg-success-950 border-success-200 dark:border-success-800';
    if (percent < 90) return 'bg-warning-50 dark:bg-warning-950 border-warning-200 dark:border-warning-800';
    return 'bg-error-50 dark:bg-error-950 border-error-200 dark:border-error-800';
  };

  const handleDeleteClick = () => {
    // Tier 1: Has transactions
    if (hasTransactions) {
      setDeleteDialogType('has-transactions');
      setDeleteDialogOpen(true);
      return;
    }

    // Tier 2: Has budget but no transactions
    if (hasBudget) {
      setDeleteDialogType('has-budget');
      setDeleteDialogOpen(true);
      return;
    }

    // Tier 3: No transactions and no budget
    setDeleteDialogType('confirm');
    setDeleteDialogOpen(true);
  };

    const handleDeleteConfirm = async () => {
      if (!costCentre) return;
    
      setIsDeleting(true);
      try {
        await deleteCostCentre(costCentre.id);
      
        onDelete?.(costCentre.id);
        setDeleteDialogOpen(false);
        onOpenChange(false);
        toast.success('Cost centre deleted successfully', {
          duration: 3000,
        });
      } catch (error) {
        console.error('Failed to delete cost centre:', error);
        toast.error('Failed to delete cost centre. Please try again.');
      } finally {
        setIsDeleting(false);
      }
    };

  const handleMarkInactive = () => {
    if (onMarkInactive) {
      onMarkInactive(costCentre.id);
      setDeleteDialogOpen(false);
      onOpenChange(false);
    }
  };

  const formatDate = (date: Date | undefined) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-2xl p-0 flex flex-col h-full">
          {/* Fixed Header */}
          <div className="border-b bg-background p-6 pb-4 flex-shrink-0">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1 flex-1">
                <SheetTitle className="text-2xl font-bold">{costCentre.name}</SheetTitle>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span className="font-sans font-medium">{costCentre.code}</span>
                  {costCentre.type && (
                    <>
                      <span>•</span>
                      <span>{costCentre.type}</span>
                    </>
                  )}
                </div>
              </div>

              {/* Top-right controls: Status Toggle + Expand Button */}
              <div className="flex items-center gap-2">
                {/* Status Toggle */}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={isActive}
                          onCheckedChange={handleStatusToggle}
                          disabled={isUpdatingStatus}
                          aria-label="Toggle cost centre status"
                        />
                        <span className={cn(
                          "text-sm font-medium transition-colors whitespace-nowrap",
                          isActive ? "text-success-600 dark:text-success-500" : "text-muted-foreground"
                        )}>
                          {isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Toggle cost centre status</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                {/* Expand to Full View Button */}
                {onExpandToFullView && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={onExpandToFullView}
                    title="Expand to full view"
                  >
                    <Maximize2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
            <TabsList className="w-full justify-start rounded-none border-b px-6 flex-shrink-0">
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="performance">Performance</TabsTrigger>
              <TabsTrigger value="insights">Insights</TabsTrigger>
            </TabsList>

            <ScrollArea className="flex-1 min-h-0">
              <ScrollAreaViewport className="h-full max-h-full">
                {/* Tab 1: Details */}
                <TabsContent value="details" className="p-6 space-y-6 mt-0">
                {/* Edit Mode Indicator */}
                {isEditMode && (
                  <div className="bg-info-50 dark:bg-info-950 border border-info-200 dark:border-info-800 rounded-lg p-3 flex items-center gap-2">
                    <Edit className="h-4 w-4 text-info-600 dark:text-info-400" />
                    <span className="text-sm font-medium text-info-900 dark:text-info-100">
                      Edit Mode - Make your changes and click Save Changes
                    </span>
                  </div>
                )}

                {/* Basic Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Basic Information</h3>
                  {!isEditMode ? (
                    <div className="grid gap-4">
                      <div className="grid grid-cols-3 gap-2">
                        <span className="text-sm text-muted-foreground">Code</span>
                        <span className="col-span-2 text-sm font-sans font-medium">{costCentre.code}</span>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <span className="text-sm text-muted-foreground">Name</span>
                        <span className="col-span-2 text-sm font-medium">{costCentre.name}</span>
                      </div>
                      {costCentre.description && (
                        <div className="grid grid-cols-3 gap-2">
                          <span className="text-sm text-muted-foreground">Description</span>
                          <span className="col-span-2 text-sm">{costCentre.description}</span>
                        </div>
                      )}
                      {costCentre.type && (
                        <div className="grid grid-cols-3 gap-2">
                          <span className="text-sm text-muted-foreground">Type</span>
                          <span className="col-span-2 text-sm">
                            <Badge variant="secondary">{costCentre.type}</Badge>
                          </span>
                        </div>
                      )}
                      {costCentre.status && (
                        <div className="grid grid-cols-3 gap-2">
                          <span className="text-sm text-muted-foreground">Status</span>
                          <span className="col-span-2 text-sm">
                            <Badge 
                              variant={
                                costCentre.status === 'In Progress' ? 'default' : 
                                costCentre.status === 'Completed' ? 'default' : 
                                'secondary'
                              }
                              className={
                                costCentre.status === 'In Progress' ? 'bg-info-100 text-info-800 dark:bg-info-900 dark:text-info-300' :
                                costCentre.status === 'Completed' ? 'bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-300' :
                                costCentre.status === 'On Hold' ? 'bg-warning-100 text-warning-800 dark:bg-warning-900 dark:text-warning-300' :
                                costCentre.status === 'Cancelled' ? 'bg-error-100 text-error-800 dark:bg-error-900 dark:text-error-300' :
                                'bg-neutral-100 text-neutral-800 dark:bg-neutral-800 dark:text-neutral-300'
                              }
                            >
                              {costCentre.status}
                            </Badge>
                          </span>
                        </div>
                      )}
                      <div className="grid grid-cols-3 gap-2">
                        <span className="text-sm text-muted-foreground">Billable</span>
                        <span className="col-span-2 text-sm">
                          {costCentre.billable ? (
                            <Badge variant="default" className="bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-300">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Yes
                            </Badge>
                          ) : (
                            <Badge variant="secondary">No</Badge>
                          )}
                        </span>
                      </div>
                      {costCentre.owner && (
                        <div className="grid grid-cols-3 gap-2">
                          <span className="text-sm text-muted-foreground">Owner</span>
                          <span className="col-span-2 text-sm">{costCentre.owner}</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="grid gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="edit-code">Code</Label>
                        <Input
                          id="edit-code"
                          value={editFormData?.code || ''}
                          onChange={(e) => updateEditField('code', e.target.value)}
                          className="font-sans"
                          disabled={isCostCentreUsed(costCentre)}
                        />
                        {isCostCentreUsed(costCentre) && (
                          <p className="text-xs text-muted-foreground">
                            Code cannot be changed once the cost centre has been used
                          </p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit-name">Name</Label>
                        <Input
                          id="edit-name"
                          value={editFormData?.name || ''}
                          onChange={(e) => updateEditField('name', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit-description">Description</Label>
                        <Textarea
                          id="edit-description"
                          value={editFormData?.description || ''}
                          onChange={(e) => updateEditField('description', e.target.value)}
                          rows={3}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit-type">Type</Label>
                        <Input
                          id="edit-type"
                          value={editFormData?.type || ''}
                          onChange={(e) => updateEditField('type', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit-status">Status</Label>
                        <Select
                          value={editFormData?.status || 'Not Started'}
                          onValueChange={(value) => updateEditField('status', value)}
                        >
                          <SelectTrigger id="edit-status">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Not Started">Not Started</SelectItem>
                            <SelectItem value="In Progress">In Progress</SelectItem>
                            <SelectItem value="On Hold">On Hold</SelectItem>
                            <SelectItem value="Completed">Completed</SelectItem>
                            <SelectItem value="Cancelled">Cancelled</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit-billable">Billable</Label>
                        <Select
                          value={editFormData?.billable ? 'yes' : 'no'}
                          onValueChange={(value) => updateEditField('billable', value === 'yes')}
                        >
                          <SelectTrigger id="edit-billable">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="yes">Yes</SelectItem>
                            <SelectItem value="no">No</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit-owner">Owner</Label>
                        <Input
                          id="edit-owner"
                          value={editFormData?.owner || ''}
                          onChange={(e) => updateEditField('owner', e.target.value)}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <Separator />

                {/* Budget Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Budget Information</h3>
                  {!isEditMode ? (
                    <div className="grid gap-4">
                      <div className="grid grid-cols-3 gap-2">
                        <span className="text-sm text-muted-foreground">Budget Amount</span>
                        <span className="col-span-2 text-sm font-semibold">{formatCurrency(costCentre.costsBudget)}</span>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <span className="text-sm text-muted-foreground">Budget Hours</span>
                        <span className="col-span-2 text-sm font-semibold">{formatHours(costCentre.hoursBudget)}</span>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <span className="text-sm text-muted-foreground">Revenue Budget</span>
                        <span className="col-span-2 text-sm font-semibold">{formatCurrency(costCentre.revenueBudget)}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="grid gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="edit-costs-budget">Budget Amount</Label>
                        <Input
                          id="edit-costs-budget"
                          type="number"
                          step="0.01"
                          value={editFormData?.costsBudget || 0}
                          onChange={(e) => updateEditField('costsBudget', parseFloat(e.target.value) || 0)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit-hours-budget">Budget Hours</Label>
                        <Input
                          id="edit-hours-budget"
                          type="number"
                          step="0.5"
                          value={editFormData?.hoursBudget || 0}
                          onChange={(e) => updateEditField('hoursBudget', parseFloat(e.target.value) || 0)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit-revenue-budget">Revenue Budget</Label>
                        <Input
                          id="edit-revenue-budget"
                          type="number"
                          step="0.01"
                          value={editFormData?.revenueBudget || 0}
                          onChange={(e) => updateEditField('revenueBudget', parseFloat(e.target.value) || 0)}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <Separator />

                {/* Timestamps */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Audit Information</h3>
                  <div className="grid gap-4">
                    {costCentre.createdAt && (
                      <div className="grid grid-cols-3 gap-2">
                        <span className="text-sm text-muted-foreground">Created</span>
                        <span className="col-span-2 text-sm">{formatDate(costCentre.createdAt)}</span>
                      </div>
                    )}
                    {costCentre.updatedAt && (
                      <div className="grid grid-cols-3 gap-2">
                        <span className="text-sm text-muted-foreground">Last Updated</span>
                        <span className="col-span-2 text-sm">{formatDate(costCentre.updatedAt)}</span>
                      </div>
                    )}
                    {!costCentre.createdAt && !costCentre.updatedAt && costCentre.lastUpdated && (
                      <div className="grid grid-cols-3 gap-2">
                        <span className="text-sm text-muted-foreground">Last Updated</span>
                        <span className="col-span-2 text-sm">{formatDate(costCentre.lastUpdated)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Custom Fields */}
                {costCentre.customFields && Object.keys(costCentre.customFields).length > 0 && (
                  <>
                    <Separator />
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold">Additional Information</h3>
                      <div className="grid gap-4">
                        {Object.entries(costCentre.customFields).map(([key, value]) => (
                          <div key={key} className="grid grid-cols-3 gap-2">
                            <span className="text-sm text-muted-foreground">{key}</span>
                            <span className="col-span-2 text-sm">
                              {typeof value === 'boolean' ? (
                                value ? (
                                  <Badge variant="default" className="bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-300">
                                    <CheckCircle2 className="h-3 w-3 mr-1" />
                                    Yes
                                  </Badge>
                                ) : (
                                  <Badge variant="secondary">No</Badge>
                                )
                              ) : value instanceof Date ? (
                                formatDate(value)
                              ) : typeof value === 'number' ? (
                                // Check if it looks like a currency value (has decimals or is large)
                                key.toLowerCase().includes('amount') || 
                                key.toLowerCase().includes('budget') || 
                                key.toLowerCase().includes('cost') || 
                                key.toLowerCase().includes('threshold') ? (
                                  formatCurrency(value)
                                ) : (
                                  value.toLocaleString()
                                )
                              ) : (
                                String(value)
                              )}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
                </TabsContent>

                {/* Tab 2: Performance */}
                <TabsContent value="performance" className="p-4 space-y-4 mt-0">
                  {/* Financial Overview - 3 Column Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {/* Costs Card */}
                    <div className="rounded-lg border p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <h4 className="text-xs font-medium text-muted-foreground uppercase">Costs</h4>
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-baseline justify-between">
                          <span className="text-xs text-muted-foreground">Budget</span>
                          <span className="text-sm font-semibold">{formatCurrency(costCentre.costsBudget)}</span>
                        </div>
                        <div className="flex items-baseline justify-between">
                          <span className="text-xs text-muted-foreground">Actual</span>
                          <span className="text-sm font-semibold">{formatCurrency(costCentre.actualCosts)}</span>
                        </div>
                      </div>
                      <Progress value={Math.min(costPercentUsed, 100)} className="h-1.5" />
                      <div className="flex items-center justify-between pt-1">
                        <span className={cn("text-xs font-medium", getBudgetStatusColor(costPercentUsed))}>
                          {formatPercent(costPercentUsed, 0)}
                        </span>
                        <Badge variant="secondary" className={cn("text-xs py-0 h-5",
                          costPercentUsed < 75 ? 'bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-300' :
                          costPercentUsed < 90 ? 'bg-warning-100 text-warning-800 dark:bg-warning-900 dark:text-warning-300' :
                          'bg-error-100 text-error-800 dark:bg-error-900 dark:text-error-300'
                        )}>
                          {costPercentUsed < 75 ? 'On Track' : costPercentUsed < 90 ? 'At Risk' : 'Over'}
                        </Badge>
                      </div>
                    </div>

                    {/* Revenue Card */}
                    <div className="rounded-lg border p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <h4 className="text-xs font-medium text-muted-foreground uppercase">Revenue</h4>
                        <TrendingUp className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-baseline justify-between">
                          <span className="text-xs text-muted-foreground">Budget</span>
                          <span className="text-sm font-semibold">{formatCurrency(costCentre.revenueBudget)}</span>
                        </div>
                        <div className="flex items-baseline justify-between">
                          <span className="text-xs text-muted-foreground">Actual</span>
                          <span className="text-sm font-semibold">{formatCurrency(costCentre.actualRevenue)}</span>
                        </div>
                      </div>
                      <Progress value={Math.min(revenuePercentAchieved, 100)} className="h-1.5" />
                      <div className="flex items-center justify-between pt-1">
                        <span className={cn("text-xs font-medium", 
                          revenuePercentAchieved >= 90 ? 'text-success-600 dark:text-success-500' :
                          revenuePercentAchieved >= 75 ? 'text-warning-600 dark:text-warning-500' :
                          'text-error-600 dark:text-error-500'
                        )}>
                          {formatPercent(revenuePercentAchieved, 0)}
                        </span>
                        <Badge variant="secondary" className={cn("text-xs py-0 h-5",
                          revenuePercentAchieved >= 90 ? 'bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-300' :
                          revenuePercentAchieved >= 75 ? 'bg-warning-100 text-warning-800 dark:bg-warning-900 dark:text-warning-300' :
                          'bg-error-100 text-error-800 dark:bg-error-900 dark:text-error-300'
                        )}>
                          {revenuePercentAchieved >= 90 ? 'Excellent' : revenuePercentAchieved >= 75 ? 'Good' : 'Below'}
                        </Badge>
                      </div>
                    </div>

                    {/* Hours Card */}
                    <div className="rounded-lg border p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <h4 className="text-xs font-medium text-muted-foreground uppercase">Hours</h4>
                        <Clock className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-baseline justify-between">
                          <span className="text-xs text-muted-foreground">Budget</span>
                          <span className="text-sm font-semibold">{costCentre.hoursBudget.toLocaleString()}h</span>
                        </div>
                        <div className="flex items-baseline justify-between">
                          <span className="text-xs text-muted-foreground">Actual</span>
                          <span className="text-sm font-semibold">{costCentre.actualHours.toLocaleString()}h</span>
                        </div>
                      </div>
                      <Progress value={Math.min(hoursPercentUsed, 100)} className="h-1.5" />
                      <div className="flex items-center justify-between pt-1">
                        <span className={cn("text-xs font-medium", getBudgetStatusColor(hoursPercentUsed))}>
                          {formatPercent(hoursPercentUsed, 0)}
                        </span>
                        <span className={cn("text-xs font-medium", remainingHours >= 0 ? 'text-success-600' : 'text-error-600')}>
                          {remainingHours >= 0 ? '+' : ''}{remainingHours.toLocaleString()}h
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Detailed Metrics - 2 Column Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {/* Cost Details */}
                    <div className="rounded-lg border p-3 space-y-2">
                      <h4 className="text-sm font-semibold">Cost Details</h4>
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">Variance</span>
                          <span className={cn("font-medium", costVariance >= 0 ? 'text-success-600' : 'text-error-600')}>
                            {costVariance >= 0 ? '+' : ''}{formatCurrency(costVariance)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">Committed</span>
                          <span className="font-medium">{formatCurrency(costCentre.committedCosts)}</span>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">Burn Rate</span>
                          <span className="font-medium">{formatCurrency(burnRate)}/mo</span>
                        </div>
                      </div>
                    </div>

                    {/* Revenue Details */}
                    <div className="rounded-lg border p-3 space-y-2">
                      <h4 className="text-sm font-semibold">Revenue Details</h4>
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">Variance</span>
                          <span className={cn("font-medium", revenueVariance >= 0 ? 'text-success-600' : 'text-error-600')}>
                            {revenueVariance >= 0 ? '+' : ''}{formatCurrency(revenueVariance)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">Remaining</span>
                          <span className="font-medium">{formatCurrency(Math.abs(remainingRevenue))}</span>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">Profit Margin</span>
                          <span className={cn("font-medium", profitMargin >= 0 ? 'text-success-600' : 'text-error-600')}>
                            {formatPercent(profitMargin, 1)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Cost Breakdown - Compact Horizontal */}
                  <div className="rounded-lg border p-3 space-y-2">
                    <h4 className="text-sm font-semibold flex items-center gap-2">
                      <BarChart3 className="h-4 w-4" />
                      Cost Breakdown
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {Object.entries(costBreakdown).map(([category, amount]) => {
                        const percentage = costCentre.actualCosts > 0 ? (amount / costCentre.actualCosts) * 100 : 0;
                        return (
                          <div key={category} className="space-y-1">
                            <div className="flex items-center justify-between text-xs">
                              <span className="font-medium capitalize">{category}</span>
                              <span className="text-muted-foreground">
                                {formatCurrency(amount)} ({formatPercent(percentage, 0)})
                              </span>
                            </div>
                            <Progress value={percentage} className="h-1" />
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Recent Transactions - Condensed */}
                  <div className="rounded-lg border">
                    <div className="flex items-center justify-between p-3 border-b">
                      <h4 className="text-sm font-semibold flex items-center gap-2">
                        <Activity className="h-4 w-4" />
                        Recent Transactions
                      </h4>
                      <Button variant="ghost" size="sm" className="h-7 text-xs" disabled>
                        View All →
                      </Button>
                    </div>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="hover:bg-transparent">
                            <TableHead className="h-8 text-xs">Date</TableHead>
                            <TableHead className="h-8 text-xs">Description</TableHead>
                            <TableHead className="h-8 text-xs">Category</TableHead>
                            <TableHead className="h-8 text-xs text-right">Amount</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {recentTransactions.slice(0, 5).map((transaction) => (
                            <TableRow key={transaction.id} className="h-9">
                              <TableCell className="text-xs py-1.5">
                                {new Date(transaction.date).toLocaleDateString('en-GB', {
                                  day: '2-digit',
                                  month: 'short',
                                })}
                              </TableCell>
                              <TableCell className="text-xs py-1.5">{transaction.description}</TableCell>
                              <TableCell className="py-1.5">
                                <Badge variant="secondary" className="text-xs py-0 h-4">{transaction.category}</Badge>
                              </TableCell>
                              <TableCell className="text-xs font-medium text-right py-1.5">
                                {formatCurrency(transaction.amount)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </TabsContent>

                {/* Tab 3: Insights */}
                <TabsContent value="insights" className="p-6 space-y-6 mt-0">
                {/* AI-Generated Insights */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Lightbulb className="h-5 w-5" />
                    Key Observations
                  </h3>
                  
                  <div className="space-y-3">
                    <div className="rounded-lg border p-4 space-y-2">
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="h-5 w-5 text-success-600 mt-0.5 shrink-0" />
                        <div>
                          <p className="font-medium">Budget Performance</p>
                          <p className="text-sm text-muted-foreground">
                            This cost centre is tracking {costPercentUsed < 100 ? 'within' : 'over'} budget at {formatPercent(costPercentUsed, 1)} utilisation.
                            {costPercentUsed < 75 && ' Performance is excellent with significant budget remaining.'}
                            {costPercentUsed >= 75 && costPercentUsed < 90 && ' Monitor closely as budget approaches limit.'}
                            {costPercentUsed >= 90 && ' Immediate attention required to prevent budget overrun.'}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-lg border p-4 space-y-2">
                      <div className="flex items-start gap-3">
                        <TrendingUp className="h-5 w-5 text-info-600 mt-0.5 shrink-0" />
                        <div>
                          <p className="font-medium">Resource Utilization</p>
                          <p className="text-sm text-muted-foreground">
                            Hours utilisation is at {formatPercent(hoursPercentUsed, 1)} with {remainingHours.toLocaleString()} hours remaining.
                            Current burn rate suggests {remainingHours > 0 ? 'completion within budget' : 'additional hours may be needed'}.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-lg border p-4 space-y-2">
                      <div className="flex items-start gap-3">
                        <BarChart3 className="h-5 w-5 text-purple-600 mt-0.5 shrink-0" />
                        <div>
                          <p className="font-medium">Cost Distribution</p>
                          <p className="text-sm text-muted-foreground">
                            Labour costs represent {formatPercent((costBreakdown.labour / costCentre.actualCosts) * 100, 0)} of total spend,
                            which is typical for {costCentre.type || 'this type of'} cost centre.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Active Alerts */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <AlertCircle className="h-5 w-5" />
                    Active Alerts
                  </h3>
                  
                  {costPercentUsed >= 90 ? (
                    <div className="rounded-lg border border-error-200 bg-error-50 dark:bg-error-950 dark:border-error-800 p-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="h-5 w-5 text-error-600 mt-0.5 shrink-0" />
                        <div>
                          <p className="font-medium text-error-900 dark:text-error-100">Budget Alert</p>
                          <p className="text-sm text-error-700 dark:text-error-300">
                            Cost centre has exceeded 90% of allocated budget. Review spending and consider budget reallocation.
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : costPercentUsed >= 75 ? (
                    <div className="rounded-lg border border-warning-200 bg-warning-50 dark:bg-warning-950 dark:border-warning-800 p-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="h-5 w-5 text-warning-600 mt-0.5 shrink-0" />
                        <div>
                          <p className="font-medium text-warning-900 dark:text-warning-100">Budget Warning</p>
                          <p className="text-sm text-warning-700 dark:text-warning-300">
                            Cost centre has reached 75% of allocated budget. Monitor spending closely.
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-lg border p-4">
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="h-5 w-5 text-success-600 mt-0.5 shrink-0" />
                        <div>
                          <p className="font-medium">No Active Alerts</p>
                          <p className="text-sm text-muted-foreground">
                            All metrics are within acceptable ranges. Continue monitoring performance.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <Separator />

                {/* Benchmarking */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Target className="h-5 w-5" />
                    Comparison to Similar Cost Centres
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="rounded-lg border p-4 space-y-2">
                      <p className="text-sm text-muted-foreground">Your Performance</p>
                      <p className="text-2xl font-bold">{formatPercent(costPercentUsed, 0)}</p>
                      <p className="text-xs text-muted-foreground">Budget Utilization</p>
                    </div>
                    <div className="rounded-lg border p-4 space-y-2">
                      <p className="text-sm text-muted-foreground">Average</p>
                      <p className="text-2xl font-bold">78%</p>
                      <p className="text-xs text-muted-foreground">Similar Cost Centres</p>
                    </div>
                  </div>

                  <div className="rounded-lg border p-4 space-y-2">
                    <p className="text-sm font-medium">Performance Ranking</p>
                    <div className="flex items-center gap-2">
                      <Progress value={65} className="h-2 flex-1" />
                      <span className="text-sm font-semibold">Top 35%</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Compared to {costCentre.type || 'similar'} cost centres in your organisation
                    </p>
                  </div>
                </div>

                <Separator />

                {/* Recommendations */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Actionable Recommendations
                  </h3>
                  
                  <div className="space-y-3">
                    {costPercentUsed >= 90 && (
                      <div className="rounded-lg border p-4 space-y-2">
                        <p className="font-medium">1. Review and Optimize Spending</p>
                        <p className="text-sm text-muted-foreground">
                          Conduct a detailed review of all expenses. Identify non-essential costs that can be deferred or eliminated.
                        </p>
                      </div>
                    )}
                    
                    <div className="rounded-lg border p-4 space-y-2">
                      <p className="font-medium">{costPercentUsed >= 90 ? '2' : '1'}. Improve Cost Tracking</p>
                      <p className="text-sm text-muted-foreground">
                        Implement weekly cost reviews to catch budget issues earlier. Set up automated alerts for unusual spending patterns.
                      </p>
                    </div>

                    <div className="rounded-lg border p-4 space-y-2">
                      <p className="font-medium">{costPercentUsed >= 90 ? '3' : '2'}. Optimize Resource Allocation</p>
                      <p className="text-sm text-muted-foreground">
                        Review team utilisation rates. Consider reallocating resources from over-staffed areas to maximise efficiency.
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Trend Analysis */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Trend Analysis
                  </h3>
                  
                  <div className="rounded-lg border p-4 space-y-4">
                    <div>
                      <p className="text-sm font-medium mb-2">Historical Pattern</p>
                      <p className="text-sm text-muted-foreground">
                        Spending has been {costPercentUsed > 80 ? 'accelerating' : 'steady'} over the past 3 months.
                        Current trajectory suggests {costPercentUsed > 90 ? 'budget overrun' : 'completion within budget'}.
                      </p>
                    </div>
                    
                    <Separator />
                    
                    <div>
                      <p className="text-sm font-medium mb-2">Projection</p>
                      <p className="text-sm text-muted-foreground">
                        At current burn rate of {formatCurrency(burnRate)}/month, estimated final cost will be{' '}
                        <span className="font-semibold">
                          {formatCurrency(costCentre.actualCosts + (burnRate * (monthsElapsed * 0.5)))}
                        </span>
                        {' '}({formatPercent((costCentre.actualCosts + (burnRate * (monthsElapsed * 0.5))) / costCentre.costsBudget * 100, 0)} of budget).
                      </p>
                    </div>
                  </div>
                </div>
                </TabsContent>
              </ScrollAreaViewport>
              <ScrollAreaScrollbar orientation="vertical" />
            </ScrollArea>
          </Tabs>

          {/* Sticky Footer - Only visible on Details tab */}
          {activeTab === 'details' && (
            <SheetFooter className="sticky bottom-0 bg-background border-t px-6 py-4 mt-auto">
              {!isEditMode ? (
                <>
                  <Button
                    variant="outline"
                    onClick={handleEnterEditMode}
                    className="flex-1"
                    disabled={isSaving || isDeleting}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleDeleteClick}
                    className="flex-1 text-error-600 hover:text-error-700 hover:bg-error-50 dark:hover:bg-error-950"
                    disabled={isSaving || isDeleting}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outline"
                    onClick={handleCancelEdit}
                    className="flex-1"
                    disabled={isSaving}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveEdit}
                    className="flex-1"
                    disabled={isSaving}
                  >
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </>
              )}
            </SheetFooter>
          )}
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation Dialogs */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteDialogType === 'has-transactions' && 'Cannot Delete Cost Centre'}
              {deleteDialogType === 'has-budget' && 'Delete Cost Centre with Budget?'}
              {deleteDialogType === 'confirm' && 'Delete Cost Centre?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteDialogType === 'has-transactions' && (
                <>
                  Cannot delete cost centre with posted transactions:
                  {costCentre.actualCosts > 0 && <div>• Actual Costs: {formatCurrency(costCentre.actualCosts)}</div>}
                  {costCentre.actualHours > 0 && <div>• Actual Hours: {formatHours(costCentre.actualHours)}</div>}
                  {costCentre.actualRevenue > 0 && <div>• Actual Revenue: {formatCurrency(costCentre.actualRevenue)}</div>}
                  <div className="mt-2">Cost centre can be marked as inactive instead.</div>
                </>
              )}
              {deleteDialogType === 'has-budget' && (
                <>
                  This cost centre has budgets assigned:
                  {costCentre.costsBudget > 0 && <div>• Cost Budget: {formatCurrency(costCentre.costsBudget)}</div>}
                  {costCentre.hoursBudget > 0 && <div>• Hours Budget: {formatHours(costCentre.hoursBudget)}</div>}
                  {costCentre.revenueBudget > 0 && <div>• Revenue Budget: {formatCurrency(costCentre.revenueBudget)}</div>}
                  <div className="mt-2">Are you sure you want to delete it?</div>
                </>
              )}
              {deleteDialogType === 'confirm' && (
                <>
                  Delete cost centre "{costCentre.name}"? This action cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {deleteDialogType === 'has-transactions' ? (
              <>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleMarkInactive}>
                  Mark as Inactive
                </AlertDialogAction>
              </>
            ) : (
              <>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDeleteConfirm}
                  className="bg-error-600 hover:bg-error-700 focus:ring-error-600"
                >
                  {deleteDialogType === 'has-budget' ? 'Delete Anyway' : 'Delete'}
                </AlertDialogAction>
              </>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
